import { Box, Chip, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import Markdown from "react-markdown";
import { ArchieChatMessage as ArchieChatMessageType } from "../../types/archie/archie";

interface Props {
  message: ArchieChatMessageType;
}

/**
 * Strips the raw search URL and surrounding "click this link:" boilerplate
 * from assistant text, since the "View Results" chip handles navigation.
 */
const cleanAssistantText = (text: string, searchUrl?: string): string => {
  if (!searchUrl) return text;

  let cleaned = text;

  // Remove the raw URL itself (may be URL-encoded or plain)
  cleaned = cleaned.replace(searchUrl, "");

  // Remove common boilerplate phrases that wrap the URL
  cleaned = cleaned.replaceAll(
    /You can view (?:these|the) interactions by clicking (?:on )?(?:the following|this) link:\s*/gi,
    ""
  );
  cleaned = cleaned.replaceAll(/by clicking (?:on )?(?:the following|this) link:\s*/gi, "");
  cleaned = cleaned.replaceAll(/clicking (?:the following|this) link:\s*/gi, "");
  cleaned = cleaned.replaceAll(/Here(?:'s| is) the link(?:\s*to view them)?:\s*/gi, "");

  // Clean up leftover whitespace and blank lines
  cleaned = cleaned.replaceAll(/\n{3,}/g, "\n\n").trim();

  return cleaned;
};

const ArchieChatMessage = ({ message }: Readonly<Props>) => {
  const navigate = useNavigate();
  const isUser = message.role === "user";
  const displayText = isUser
    ? message.text
    : cleanAssistantText(message.text, message.searchUrl);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-start" : "flex-end",
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          maxWidth: "85%",
          px: 2,
          py: 1,
          borderRadius: 2,
          backgroundColor: isUser ? "grey.100" : "primary.main",
          color: isUser ? "text.primary" : "white",
        }}
      >
        {isUser ? (
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {displayText}
          </Typography>
        ) : (
          <Box
            sx={{
              wordBreak: "break-word",
              fontSize: "0.875rem",
              lineHeight: 1.5,
              "& p": { m: 0, mb: 1, "&:last-child": { mb: 0 } },
              "& h1, & h2, & h3, & h4, & h5, & h6": {
                mt: 1,
                mb: 0.5,
                fontWeight: 600,
                lineHeight: 1.3,
              },
              "& h2": { fontSize: "1rem" },
              "& h3": { fontSize: "0.9375rem" },
              "& ul, & ol": { m: 0, mb: 1, pl: 2.5 },
              "& li": { mb: 0.25 },
              "& hr": {
                border: "none",
                borderTop: "1px solid rgba(255,255,255,0.3)",
                my: 1,
              },
              "& strong": { fontWeight: 700 },
              "& code": {
                backgroundColor: "rgba(255,255,255,0.15)",
                borderRadius: "3px",
                px: 0.5,
                fontSize: "0.8125rem",
                fontFamily: "monospace",
              },
              "& a": { color: "inherit", textDecoration: "underline" },
            }}
          >
            <Markdown>{displayText}</Markdown>
          </Box>
        )}

        {message.searchUrl && (
          <Chip
            icon={<SearchIcon sx={{ color: "inherit" }} />}
            label="View Results"
            size="small"
            onClick={() => navigate(message.searchUrl!)}
            sx={{
              mt: 1,
              cursor: "pointer",
              backgroundColor: isUser ? "primary.main" : "rgba(255,255,255,0.2)",
              color: "white",
              "&:hover": {
                backgroundColor: isUser
                  ? "primary.dark"
                  : "rgba(255,255,255,0.35)",
              },
            }}
          />
        )}

        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.5,
            opacity: 0.7,
            color: isUser ? "text.secondary" : "rgba(255,255,255,0.7)",
          }}
        >
          {message.timestamp.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Typography>
      </Box>
    </Box>
  );
};

export default ArchieChatMessage;
